import React, { InputHTMLAttributes } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { mix } from 'polished'
import { withBox } from 'components/Box'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  small?: boolean
  disabled?: boolean
  invalid?: boolean
  hasIcon?: boolean
  icon?: React.ReactElement
  /**
   * This boolean flag is used for Stripe input fields, where there are two different inputs.
   * 1. The outer input is controlled by us and has the look&feel we want.
   * 2. The inner input is coming from Stripe (from an iframe) and it's the one the user interacts with.
   *
   * We need to listen to changes on the focus of the inner input to make the outer component look like it's focused as
   * well. That is when this flag comes into play.
   */
  stripeFakeFocus?: boolean
}

export const borderInputStyle = css<InputProps>`
  ${({ small, disabled, invalid, theme, hasIcon }) => css`
    width: 100%;
    padding: ${small
      ? hasIcon
        ? `10px 12px 10px 42px`
        : `0.4375rem 16px`
      : hasIcon
      ? `10px 12px 10px 42px`
      : `10px 12px`};

    border: 1px solid ${theme.colors.border[2]};
    border-radius: 6px;

    transition: border-color ${theme.transitions.duration[1]}
        ${theme.transitions.timing.easeInOut},
      padding ${theme.transitions.duration[4]}
        ${theme.transitions.timing.easeOutExponential};

    ::placeholder {
      color: ${theme.colors.foreground.secondary[4]};
      opacity: 1; /* Override Firefox default */
    }

    ::selection {
      /* We can't use the alpha channel in ::selection so we are mixing it with white */
      background-color: ${invalid
        ? mix(0.35, theme.colors.negative, theme.colors.background.secondary[1])
        : mix(
            0.35,
            theme.colors.primaryTint[100],
            theme.colors.background.secondary[1]
          )};
    }

    /* Add the disabled look and feel to the stripe inputs */
    ${disabled &&
    css`
      color: ${theme.colors.foreground.secondary[4]};
      background-color: ${theme.colors.background.secondary[2]};

      border: 1px dashed ${theme.colors.border[1]};
    `};

    ${invalid &&
    css`
      border: 2px solid ${theme.colors.state.negative};
    `};

    &:focus,
    &.StripeElement--focus {
      ${invalid &&
      css`
        border: none;
      `};
    }

    &.StripeElement--invalid {
      ${css`
        border: 2px solid ${theme.colors.state.negative};
      `};
    }
  `}
`

export const baseInputStyles = css<InputProps>`
  ${({ small, disabled, stripeFakeFocus, theme }) => css`
    display: block;
    position: relative;

    height: ${small ? `38px` : `40px`};
    width: 100%;
    padding: ${small ? `8px` : `10px 12px;`};

    background-color: ${theme.colors.background.secondary[2]};
    color: ${theme.colors.foreground.secondary[1]};
    appearance: none;

    font-size: ${theme.fontSizes.small};

    ${!disabled &&
    css`
      box-shadow: ${theme.shadows.boxShadow.inner};
    `}

    ${stripeFakeFocus &&
    css`
      background-color: ${theme.colors.background.secondary[1]};
    `}

    &:focus {
      background-color: ${theme.colors.background.secondary[1]};
    }

    &:disabled {
      color: ${theme.colors.foreground.secondary[4]};
      background-color: ${theme.colors.background.secondary[2]};
    }

    /**
     * Work-around for the border animation noted in the issue
     * https://github.com/sketch-hq/Cloud/issues/595.
     *
     * Because the initial border color is "black" when adding style,
     * do to "transition: border-color 0.15s ease-in-out;" the color will animate
     * to the final color.
     *
     * making sure that the color is already the correct one before appending other classes
     * prevents that.
     */
    border-color: ${theme.colors.border[2]};

    &[type='text'],
    &[type='email'],
    &[type='password'],
    &[type='search'],
    &[type='button'],
    &[type='number'],
    &.StripeElement {
      ${borderInputStyle}
    }
  `}
`

const InputWithBox = styled(withBox('input'))<InputProps>`
  ${baseInputStyles}
`

const fadeInOut = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`

const IconWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 12px;
  display: flex;
  transform: translateY(-50%);
  z-index: 1;
  color: ${({ theme }) => theme.colors.foreground.secondary[4]};
  opacity: 1;

  animation: ${fadeInOut} ${({ theme }) => theme.transitions.duration[2]};
`

interface StyledInputProps extends InputProps {
  icon?: React.ReactElement
}

type InputRef = HTMLInputElement

const StyledInput = React.forwardRef<InputRef, StyledInputProps>(
  function StyledInput(props, ref) {
    const { icon: Icon, value, ...remainingProps } = props

    const isValueEmpty = !value?.toString().length
    const hasIcon = isValueEmpty && !!Icon

    return (
      <Wrapper>
        {hasIcon && <IconWrapper>{Icon}</IconWrapper>}
        <InputWithBox
          ref={ref}
          hasIcon={hasIcon}
          value={value}
          {...remainingProps}
        />
      </Wrapper>
    )
  }
)

export default styled(StyledInput)``
